import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIsFetching } from 'react-query';
import { useDispatch } from 'react-redux';
import { useEffectOnce } from 'react-use';
import { cardAnimationVariants } from '../../../../BeFi20/BeFi20.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { FullPageLoading } from '../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { VisualPicker } from '../../../../CommonComponents/OPL/VisualPicker/VisualPicker';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { AddAccountSelectionModalWrapper } from '../../../../Profile/Accounts/components/AddAccountSelectionModalWrapper/AddAccountSelectionModalWrapper';
import * as Actions from '../../../../Profile/ProfileActions';
import { queryKeys } from '../../../api/queryKeys';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';
import { StepCard } from '../../../PulseCheck.styles';
import { useSetGoalContext } from '../../context/useSetGoalContext';
import { FORM_ID } from '../../SetGoal.meta';
import { FormValues } from './Accounts.meta';
import { AccountModalUsageContext } from '../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/context/AccountFormProvider.meta';

interface Props {
  clientId: string;
}

export const Accounts = ({ clientId }: Props) => {
  const { wizard, setWizard, options, onSuccess } = useSetGoalContext();

  const [isOpenAddAccountModal, setIsOpenAddAccountModal] = useState(false);

  const isFetchingOptions = useIsFetching(queryKeys.goalsOptionsAll()) > 0;

  const dispatch = useDispatch();

  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      accountIds: wizard.accountIds || [],
    },
  });

  /**
   * Effect to allow opening the Add Account Modal
   */
  useEffectOnce(() => {
    dispatch(
      Actions.initializeSection(clientId, ADV.util.env('isAdviserArea'), 'accountsReact', 0, 0),
    );
  });

  /**
   * Effect to allow auto-selecting newly added accounts
   */
  const accountIdsRef = useRef(options.Accounts.map(({ Id }) => Id));
  useEffect(() => {
    if (options.Accounts.length !== accountIdsRef.current.length) {
      const newAccountIds = options.Accounts.reduce((acc, { Id }) => {
        if (!accountIdsRef.current.includes(Id)) {
          acc.push(Id);
        }

        return acc;
      }, [] as string[]);

      setValue('accountIds', [...watch('accountIds'), ...newAccountIds]);

      accountIdsRef.current = options.Accounts.map(({ Id }) => Id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.Accounts]);

  const onSubmit = handleSubmit(values => {
    const newWizard = { ...wizard, ...values };

    setWizard(newWizard);

    onSuccess(newWizard);
  });

  return (
    <>
      {isFetchingOptions && <FullPageLoading />}

      <StepWrapper as="form" id={FORM_ID} onSubmit={onSubmit}>
        <StepCard
          variants={cardAnimationVariants}
          initial="enter"
          animate="visible"
          css="overflow-y: auto"
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Text as="h1" fontSize="h4" fontWeight={600}>
              Which account(s) will fund this goal?
            </Text>

            <Button
              variant="link"
              leftIcon="fa fa-plus"
              onClick={() => {
                setIsOpenAddAccountModal(true);
              }}
            >
              Add Account
            </Button>
          </Box>

          {options.Accounts.length > 0 && (
            <Controller
              control={control}
              name="accountIds"
              render={({ field: { value: accountIds, onChange } }) => (
                <Box display="flex" flexDirection="column" mt="16" css="gap: 16px">
                  {options.Accounts.map(({ Id, Name, Institution, Balance }) => (
                    <VisualPicker
                      key={Id}
                      variant="checkbox"
                      label={Name}
                      helperText={Institution}
                      rightText={formatCurrency(Balance)}
                      onChange={evt => {
                        if (evt.target.checked) {
                          onChange([...accountIds, Id]);
                        } else {
                          onChange(accountIds.filter(accountId => accountId !== Id));
                        }
                      }}
                      checked={accountIds.includes(Id)}
                    />
                  ))}
                </Box>
              )}
            />
          )}
        </StepCard>
      </StepWrapper>

      {isOpenAddAccountModal && (
        <AddAccountSelectionModalWrapper
          usageContext={AccountModalUsageContext.LargePurchaseWorkflow}
          showExternalProviders={false}
          onClose={() => setIsOpenAddAccountModal(false)}
        />
      )}
    </>
  );
};
